export interface RegulatorySignal {
  description: string;
  daysAgo: number;
}

export interface CountryData {
  name: string;
  code: string;
  score: number;
  delta30d: number;
  drivers: string[];
  latestSignal: RegulatorySignal;
}

// =============================================================================
// PRESSURE STATUS (Higher score = Higher pressure = Red)
// =============================================================================

export type PressureStatus = 'stable' | 'watch' | 'tightening' | 'high';

export const getStatus = (score: number): PressureStatus => {
  // Aligned with backend thresholds (scoring_engine.py STATUS_THRESHOLDS)
  if (score < 35) return 'stable';   // 0-34
  if (score < 55) return 'watch';    // 35-54
  if (score < 75) return 'tightening'; // 55-74
  return 'high';                      // 75-100
};

export const getStatusLabel = (status: PressureStatus): string => {
  const labels: Record<PressureStatus, string> = {
    stable: 'Stable',
    watch: 'Watch',
    tightening: 'Tightening',
    high: 'High Pressure',
  };
  return labels[status];
};

export const getStatusColor = (status: PressureStatus): string => {
  const colors: Record<PressureStatus, string> = {
    stable: '#3d9970',
    watch: '#e6a23c',
    tightening: '#d4712a',
    high: '#c9524a',
  };
  return colors[status];
};

// =============================================================================
// OPPORTUNITY STATUS (Higher score = Higher opportunity = Green)
// =============================================================================

export type OpportunityStatus = 'low' | 'moderate' | 'good' | 'excellent';

export const getOpportunityStatus = (score: number): OpportunityStatus => {
  // Aligned with backend thresholds (inverted from pressure)
  if (score < 35) return 'low';       // 0-34
  if (score < 55) return 'moderate';  // 35-54
  if (score < 75) return 'good';      // 55-74
  return 'excellent';                  // 75-100
};

export const getOpportunityStatusLabel = (status: OpportunityStatus): string => {
  const labels: Record<OpportunityStatus, string> = {
    low: 'Low',
    moderate: 'Moderate',
    good: 'Good',
    excellent: 'Excellent',
  };
  return labels[status];
};

/**
 * Opportunity colors are INVERTED from pressure colors
 * Higher opportunity (green) is good, lower opportunity (red) is bad
 */
export const getOpportunityStatusColor = (status: OpportunityStatus): string => {
  const colors: Record<OpportunityStatus, string> = {
    low: '#c9524a',      // Red - low opportunity
    moderate: '#d4712a', // Dark orange
    good: '#e6a23c',     // Orange
    excellent: '#3d9970', // Green - excellent opportunity
  };
  return colors[status];
};

// =============================================================================
// TRAJECTORY INDICATORS
// =============================================================================

export type Trajectory = 'opening' | 'stable' | 'tightening' | 'closing';

export const getTrajectoryLabel = (trajectory: Trajectory): string => {
  const labels: Record<Trajectory, string> = {
    opening: 'Opening',
    stable: 'Stable',
    tightening: 'Tightening',
    closing: 'Closing',
  };
  return labels[trajectory];
};

export const getTrajectoryArrow = (trajectory: Trajectory): string => {
  const arrows: Record<Trajectory, string> = {
    opening: '↗',
    stable: '→',
    tightening: '↘',
    closing: '⊗',
  };
  return arrows[trajectory];
};

export const getTrajectoryColor = (trajectory: Trajectory): string => {
  const colors: Record<Trajectory, string> = {
    opening: '#3d9970',  // Green - market opening
    stable: '#6b7280',   // Gray - no change
    tightening: '#e6a23c', // Orange - caution
    closing: '#c9524a',  // Red - closing/restricting
  };
  return colors[trajectory];
};

// =============================================================================
// REGULATORY STATUS INDICATORS
// =============================================================================

export type RegulatoryStatusType = 'open' | 'regulated' | 'restricted' | 'prohibited';

export const getRegulatoryStatusLabel = (status: RegulatoryStatusType): string => {
  const labels: Record<RegulatoryStatusType, string> = {
    open: 'Open',
    regulated: 'Regulated',
    restricted: 'Restricted',
    prohibited: 'Prohibited',
  };
  return labels[status];
};

export const getRegulatoryStatusColor = (status: RegulatoryStatusType): string => {
  const colors: Record<RegulatoryStatusType, string> = {
    open: '#3d9970',     // Green
    regulated: '#4a9fd4', // Blue
    restricted: '#e6a23c', // Orange
    prohibited: '#c9524a', // Red
  };
  return colors[status];
};

// =============================================================================
// VIEW MODE TYPES
// =============================================================================

export type ViewMode = 'pressure' | 'opportunity';

export interface ViewModeConfig {
  label: string;
  description: string;
  getColor: (score: number) => string;
  getStatusLabel: (score: number) => string;
  legendLabels: string[];
}

export const viewModeConfigs: Record<ViewMode, ViewModeConfig> = {
  pressure: {
    label: 'Regulatory Pressure',
    description: 'Where is risk rising?',
    getColor: (score: number) => getStatusColor(getStatus(score)),
    getStatusLabel: (score: number) => getStatusLabel(getStatus(score)),
    legendLabels: ['Stable', 'Watch', 'Tightening', 'High Pressure'],
  },
  opportunity: {
    label: 'Market Opportunity',
    description: 'Where to focus?',
    getColor: (score: number) => getOpportunityStatusColor(getOpportunityStatus(score)),
    getStatusLabel: (score: number) => getOpportunityStatusLabel(getOpportunityStatus(score)),
    legendLabels: ['Low', 'Moderate', 'Good', 'Excellent'],
  },
};

export const regulatoryData: Record<string, CountryData> = {
  GB: {
    name: 'United Kingdom',
    code: 'GB',
    score: 45,
    delta30d: 3,
    drivers: [
      'Gambling Commission review ongoing',
      'Affordability check proposals',
      'Increased operator scrutiny',
    ],
    latestSignal: {
      description: 'UKGC consultation on enhanced customer checks',
      daysAgo: 5,
    },
  },
  MT: {
    name: 'Malta',
    code: 'MT',
    score: 28,
    delta30d: -2,
    drivers: [
      'Stable licensing framework',
      'Pro-industry government stance',
      'EU compliance focus',
    ],
    latestSignal: {
      description: 'MGA issued updated technical standards guidance',
      daysAgo: 12,
    },
  },
  DE: {
    name: 'Germany',
    code: 'DE',
    score: 78,
    delta30d: 6,
    drivers: [
      'Increased enforcement actions',
      'Draft advertising restrictions',
      'Negative regulator rhetoric trend',
    ],
    latestSignal: {
      description: 'Regulator statement on market tightening',
      daysAgo: 3,
    },
  },
  NL: {
    name: 'Netherlands',
    code: 'NL',
    score: 65,
    delta30d: 4,
    drivers: [
      'Strict advertising ban enforcement',
      'License revocation threats',
      'Political pressure on gambling',
    ],
    latestSignal: {
      description: 'KSA announced new advertising compliance audits',
      daysAgo: 7,
    },
  },
  SE: {
    name: 'Sweden',
    code: 'SE',
    score: 58,
    delta30d: 2,
    drivers: [
      'Bonus restriction proposals',
      'Deposit limit discussions',
      'Responsible gambling focus',
    ],
    latestSignal: {
      description: 'Parliamentary debate on deposit caps',
      daysAgo: 4,
    },
  },
  ES: {
    name: 'Spain',
    code: 'ES',
    score: 42,
    delta30d: -1,
    drivers: [
      'Advertising watershed rules stable',
      'DGOJ enforcement consistent',
      'Regional licensing considerations',
    ],
    latestSignal: {
      description: 'DGOJ published annual compliance report',
      daysAgo: 15,
    },
  },
  IT: {
    name: 'Italy',
    code: 'IT',
    score: 52,
    delta30d: 5,
    drivers: [
      'Tax reform discussions',
      'Concession renewal uncertainty',
      'Advertising restrictions debate',
    ],
    latestSignal: {
      description: 'Government proposed gaming tax adjustments',
      daysAgo: 8,
    },
  },
  PT: {
    name: 'Portugal',
    code: 'PT',
    score: 35,
    delta30d: 0,
    drivers: [
      'Stable regulatory environment',
      'SRIJ licensing routine',
      'Limited political attention',
    ],
    latestSignal: {
      description: 'SRIJ issued new operator license',
      daysAgo: 20,
    },
  },
  US: {
    name: 'United States',
    code: 'US',
    score: 38,
    delta30d: -3,
    drivers: [
      'State-by-state expansion continues',
      'Federal framework unlikely near-term',
      'Tribal gaming negotiations ongoing',
    ],
    latestSignal: {
      description: 'New state legalization bill introduced',
      daysAgo: 6,
    },
  },
  CA: {
    name: 'Canada',
    code: 'CA',
    score: 32,
    delta30d: 1,
    drivers: [
      'Ontario market maturing',
      'Provincial licensing stable',
      'Responsible gambling initiatives',
    ],
    latestSignal: {
      description: 'AGCO updated responsible gambling guidelines',
      daysAgo: 10,
    },
  },
  FRA: {
    name: 'France',
    code: 'FRA',
    score: 48,
    delta30d: 2,
    drivers: [
      'ANJ enforcement active',
      'Sports betting focus',
      'Advertising restrictions in place',
    ],
    latestSignal: {
      description: 'ANJ issued operator warning on compliance',
      daysAgo: 9,
    },
  },
  AUT: {
    name: 'Austria',
    code: 'AUT',
    score: 62,
    delta30d: 8,
    drivers: [
      'Licensing framework uncertain',
      'Court rulings pending',
      'EU infringement pressure',
    ],
    latestSignal: {
      description: 'Court ruling on licensing expected soon',
      daysAgo: 2,
    },
  },
  BEL: {
    name: 'Belgium',
    code: 'BEL',
    score: 55,
    delta30d: 3,
    drivers: [
      'Advertising ban expansion',
      'Deposit limit proposals',
      'Political gambling debate',
    ],
    latestSignal: {
      description: 'Minister proposed stricter advertising rules',
      daysAgo: 11,
    },
  },
  GRC: {
    name: 'Greece',
    code: 'GRC',
    score: 40,
    delta30d: -2,
    drivers: [
      'Licensing regime established',
      'Tax framework stable',
      'Enforcement improving',
    ],
    latestSignal: {
      description: 'HGC granted additional operator licenses',
      daysAgo: 14,
    },
  },
  DNK: {
    name: 'Denmark',
    code: 'DNK',
    score: 44,
    delta30d: 1,
    drivers: [
      'Spillemyndigheden oversight active',
      'Responsible gambling emphasis',
      'Stable market conditions',
    ],
    latestSignal: {
      description: 'DGA published market performance data',
      daysAgo: 18,
    },
  },
  FIN: {
    name: 'Finland',
    code: 'FIN',
    score: 70,
    delta30d: 5,
    drivers: [
      'Monopoly reform uncertainty',
      'EU pressure on Veikkaus',
      'Liberalization debates',
    ],
    latestSignal: {
      description: 'Government committee reviewing gambling law',
      daysAgo: 4,
    },
  },
  NOR: {
    name: 'Norway',
    code: 'NOR',
    score: 72,
    delta30d: 4,
    drivers: [
      'DNS blocking enforcement',
      'Payment blocking active',
      'Strong monopoly defense',
    ],
    latestSignal: {
      description: 'Lotteri- og stiftelsestilsynet blocked new sites',
      daysAgo: 3,
    },
  },
  CHE: {
    name: 'Switzerland',
    code: 'CHE',
    score: 50,
    delta30d: 0,
    drivers: [
      'ESBK enforcement stable',
      'Blocking measures in place',
      'Cantonal variations',
    ],
    latestSignal: {
      description: 'ESBK published annual supervision report',
      daysAgo: 22,
    },
  },
  IRL: {
    name: 'Ireland',
    code: 'IRL',
    score: 54,
    delta30d: 7,
    drivers: [
      'Gambling Regulatory Authority active',
      'New legislation implementation',
      'Advertising restrictions coming',
    ],
    latestSignal: {
      description: 'GRA announced advertising code review',
      daysAgo: 6,
    },
  },
  POL: {
    name: 'Poland',
    code: 'POL',
    score: 60,
    delta30d: 3,
    drivers: [
      'Strict online restrictions',
      'Land-based focus',
      'Limited licensing',
    ],
    latestSignal: {
      description: 'Ministry proposed online market expansion study',
      daysAgo: 13,
    },
  },
  CZE: {
    name: 'Czech Republic',
    code: 'CZE',
    score: 38,
    delta30d: -1,
    drivers: [
      'Mature market framework',
      'Stable taxation',
      'Moderate enforcement',
    ],
    latestSignal: {
      description: 'Ministry published licensing statistics',
      daysAgo: 16,
    },
  },
  ROU: {
    name: 'Romania',
    code: 'ROU',
    score: 45,
    delta30d: 2,
    drivers: [
      'ONJN oversight active',
      'Tax adjustments proposed',
      'Advertising discussions',
    ],
    latestSignal: {
      description: 'ONJN issued new compliance guidance',
      daysAgo: 8,
    },
  },
  BGR: {
    name: 'Bulgaria',
    code: 'BGR',
    score: 42,
    delta30d: 0,
    drivers: [
      'Licensing framework operational',
      'Moderate enforcement',
      'Regional market focus',
    ],
    latestSignal: {
      description: 'Commission renewed operator licenses',
      daysAgo: 25,
    },
  },
  AUS: {
    name: 'Australia',
    code: 'AUS',
    score: 68,
    delta30d: 6,
    drivers: [
      'Advertising ban expansion',
      'Inducement restrictions',
      'ACMA enforcement active',
    ],
    latestSignal: {
      description: 'Government announced gambling advertising review',
      daysAgo: 2,
    },
  },
  BRA: {
    name: 'Brazil',
    code: 'BRA',
    score: 30,
    delta30d: -5,
    drivers: [
      'Market liberalization ongoing',
      'Regulatory framework developing',
      'Strong growth outlook',
    ],
    latestSignal: {
      description: 'Senate approved iGaming regulation bill',
      daysAgo: 4,
    },
  },
  JPN: {
    name: 'Japan',
    code: 'JPN',
    score: 25,
    delta30d: 0,
    drivers: [
      'IR development focus',
      'Online gaming restricted',
      'Pachinko exemptions',
    ],
    latestSignal: {
      description: 'Osaka IR project timeline confirmed',
      daysAgo: 30,
    },
  },
  SGP: {
    name: 'Singapore',
    code: 'SGP',
    score: 35,
    delta30d: 1,
    drivers: [
      'Strict casino regulation',
      'Entry levy system',
      'Responsible gambling focus',
    ],
    latestSignal: {
      description: 'GRA published annual regulatory review',
      daysAgo: 19,
    },
  },
  ZAF: {
    name: 'South Africa',
    code: 'ZAF',
    score: 48,
    delta30d: 3,
    drivers: [
      'Online betting regulation',
      'Provincial licensing variance',
      'Sports betting growth',
    ],
    latestSignal: {
      description: 'NGB proposed unified online framework',
      daysAgo: 11,
    },
  },
};
